import React, { useState } from 'react';
import { Button } from 'equasion-lib';
import { FiAlertTriangle, FiChevronRight, FiLink, FiMaximize, FiX } from 'react-icons/fi';
import {
  combineAlertCopy,
  combineAlertOrders,
  combineParent,
  combineShipmentSummary,
  combineStageText,
  soLabel,
  type CombineContext,
  type CombineOrder,
  type CombineVariant
} from '@/lib/combineShipment';
import './CombineShipment.scss';

// Warehouse is on react-icons 4.x; RiArchiveStackLine only shipped in 5.x (as ShippingBundlePanel).
const RiArchiveStackLine = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M4 5H20V3H4V5ZM20 9H4V7H20V9ZM3 11H10V13H14V11H21V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V11ZM16 13V15H8V13H5V19H19V13H16Z" />
  </svg>
);

const click = (onClick: () => void) => ({ onClick });

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The progress bar on a row at Verify: how much of the order is scanned. */
function VerifyProgress({ order }: { order: CombineOrder }) {
  if (order.stage !== 'verifying' || !order.requiredUnits) return null;
  const pct = Math.min(100, Math.round(((order.verifiedUnits ?? 0) / order.requiredUnits) * 100));
  return (
    <span className="combine-card__progress" aria-hidden="true">
      <span style={{ width: `${pct}%` }} />
    </span>
  );
}

/**
 * The combine step on the Pick and Verify screens, in two parts — each renders only when it has
 * something to say, so an order with nothing to combine looks exactly as it always has:
 *
 *   ALERT     — the client has other orders still being picked that could ship with this one
 *               (amber). Pick: "Set this order aside". Verify: "Another order is coming". The
 *               primary action scans the other order's pick ticket; each row can also be combined
 *               with one tap.
 *   SHIPMENT  — the orders already combined with this one (the Ship step's bundle blue): the parent
 *               first, each with where it stands (being picked / verifying n of m / at Ship). A row
 *               opens that order; × takes it out of the shipment (confirmed first).
 */
function CombineShipmentCard({
  variant,
  context,
  busy = false,
  onScan,
  onCombineOrder,
  onOpenOrder,
  onRemove
}: {
  variant: CombineVariant;
  context: CombineContext | null;
  busy?: boolean;
  onScan: () => void;
  onCombineOrder: (order: CombineOrder) => void;
  onOpenOrder: (order: CombineOrder) => void;
  onRemove: (order: CombineOrder) => void;
}) {
  const [confirmRemove, setConfirmRemove] = useState<CombineOrder | null>(null);
  const alertOrders = combineAlertOrders(context);
  const shipment = context?.shipment || null;
  if (!context || (!alertOrders.length && !shipment)) return null;

  const copy = combineAlertCopy(variant, context);
  const parent = combineParent(context);
  const current = shipment?.orders.find((o) => o.isCurrent) || null;

  const toteNote =
    variant === 'pick' && shipment
      ? parent && !parent.isCurrent
        ? `Put this tote with ${soLabel(parent)} — it is verified and shipped with it.`
        : 'The other orders are combined into this one — keep their totes together.'
      : null;

  return (
    <section className="combine-card" aria-label="Combined shipment">
      {alertOrders.length > 0 && (
        <div className="combine-card__alert" role="alert">
          <header className="combine-card__alert-head">
            <FiAlertTriangle aria-hidden="true" />
            <strong>{copy.title}</strong>
          </header>
          <p className="combine-card__alert-text">{copy.text}</p>
          <ul className="combine-card__rows combine-card__rows--alert">
            {alertOrders.map((order) => (
              <li key={order.id} className="combine-card__row">
                <span className="combine-card__so">{order.vendorSalesOrderNumber}</span>
                <span className="combine-card__stage">{combineStageText(order)}</span>
                <span className="combine-card__count">{plural(order.itemCount, 'item', 'items')}</span>
                <button
                  type="button"
                  className="combine-card__row-action"
                  disabled={busy}
                  onClick={() => onCombineOrder(order)}
                  aria-label={`Combine ${soLabel(order)} with this order`}
                >
                  <FiLink aria-hidden="true" /> Combine
                </button>
              </li>
            ))}
          </ul>
          <Button
            type="primary"
            fullWidth
            iconBefore={<FiMaximize />}
            disabled={busy}
            {...click(onScan)}
          >
            Scan Pick Ticket to Combine
          </Button>
        </div>
      )}

      {shipment && (
        <div className="combine-card__shipment">
          <header className="combine-card__eyebrow">
            <RiArchiveStackLine aria-hidden="true" />
            <span className="combine-card__eyebrow-label">Combined shipment</span>
            <span className="combine-card__eyebrow-id">{shipment.bundleNumber}</span>
          </header>
          <ol className="combine-card__rows">
            {shipment.orders.map((order) => {
              const tag = order.isCurrent ? 'This order' : order.isParent ? 'Parent' : null;
              return (
                <li
                  key={order.id}
                  className={[
                    'combine-card__row',
                    'combine-card__row--member',
                    order.isCurrent && 'is-current',
                    `stage-${order.stage}`
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <button
                    type="button"
                    className="combine-card__row-main"
                    disabled={order.isCurrent}
                    onClick={() => onOpenOrder(order)}
                    aria-label={order.isCurrent ? undefined : `Open ${soLabel(order)}`}
                  >
                    <span className="combine-card__so">{order.vendorSalesOrderNumber}</span>
                    <span className="combine-card__tags">
                      {tag && <span className="combine-card__tag">{tag}</span>}
                      {order.isParent && order.isCurrent && (
                        <span className="combine-card__tag">Parent</span>
                      )}
                    </span>
                    <span className="combine-card__stage">{combineStageText(order)}</span>
                    <VerifyProgress order={order} />
                    {!order.isCurrent && <FiChevronRight className="combine-card__go" aria-hidden="true" />}
                  </button>
                  <button
                    type="button"
                    className="combine-card__remove"
                    disabled={busy}
                    onClick={() => setConfirmRemove(order)}
                    aria-label={`Remove ${soLabel(order)} from the shipment`}
                  >
                    <FiX aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ol>
          {confirmRemove ? (
            <div className="combine-card__confirm" role="alertdialog" aria-label="Remove from shipment">
              <p>
                {confirmRemove.isCurrent
                  ? `Take this order out of ${shipment.bundleNumber}? It is verified and shipped on its own.`
                  : `Take ${soLabel(confirmRemove)} out of ${shipment.bundleNumber}? It is verified and shipped on its own.`}
              </p>
              <div className="combine-card__confirm-actions">
                <Button
                  type="danger"
                  disabled={busy}
                  {...click(() => {
                    const order = confirmRemove;
                    setConfirmRemove(null);
                    onRemove(order);
                  })}
                >
                  Remove
                </Button>
                <Button type="secondary" {...click(() => setConfirmRemove(null))}>
                  Keep
                </Button>
              </div>
            </div>
          ) : (
            <footer className="combine-card__foot">
              <p>{toteNote || combineShipmentSummary(shipment)}</p>
              {!alertOrders.length && current && (
                <button type="button" className="combine-card__more" disabled={busy} onClick={onScan}>
                  <FiMaximize aria-hidden="true" /> Combine another order
                </button>
              )}
            </footer>
          )}
        </div>
      )}
    </section>
  );
}

export default CombineShipmentCard;
